import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchDenmarkTaxonomyByCodeApi,
  DenmarkTaxonomyCodeFetchItem,
} from "@/database";
import TaxonomySearch from "./TaxonomySearch";

export type DenmarkTaxonomyChip = {
  id: string | number;
  name: string;
  code: string;
};

function taxonomyFetchRowToPath(row: DenmarkTaxonomyCodeFetchItem): string {
  const parts = [...(row.ancestors ?? []).map((a) => a.name), row.name].filter(
    Boolean
  );
  return parts.join(" > ");
}

function normTaxonomyCode(code: string | number | undefined): string {
  return String(code ?? "").trim();
}

function taxonomyAncestorCodesFromRow(
  row: DenmarkTaxonomyCodeFetchItem
): string[] {
  return (row.ancestors ?? [])
    .map((a) => normTaxonomyCode(a.code))
    .filter(Boolean);
}

function computeTaxonomyHierarchyConflictIds(
  selected: { id: string | number; code?: string }[],
  ancestorCodesById: Record<string, string[]>
): Set<string> {
  const conflict = new Set<string>();
  if (selected.length < 2) return conflict;

  for (let i = 0; i < selected.length; i++) {
    for (let j = 0; j < selected.length; j++) {
      if (i === j) continue;
      const maybeParent = selected[i];
      const maybeChild = selected[j];
      const parentCode = normTaxonomyCode(maybeParent.code);
      if (!parentCode) continue;

      const childKey = String(maybeChild.id);
      const ancestorsOfChild = ancestorCodesById[childKey];
      if (!ancestorsOfChild?.length) continue;

      if (ancestorsOfChild.some((ac) => normTaxonomyCode(ac) === parentCode)) {
        conflict.add(String(maybeParent.id));
        conflict.add(String(maybeChild.id));
      }
    }
  }
  return conflict;
}

export interface DenmarkTaxonomySectionProps {
  selectedTaxonomy: DenmarkTaxonomyChip[];
  denmarkTaxonomyList: any[];
  denmarkTaxonomyListLoading?: boolean;
  getDenmarkTaxonomyList: (params: any) => void;
  selectedHost?: string;
  selectedChip: number | null | undefined;
  onUpdateTaxonomy: (taxonomy: DenmarkTaxonomyChip[]) => void;
  /** Fired when branch (parent/child) conflict among selected taxonomies changes. */
  onBranchConflictChange?: (hasConflict: boolean) => void;
}

const DenmarkTaxonomySection: React.FC<DenmarkTaxonomySectionProps> = ({
  selectedTaxonomy,
  denmarkTaxonomyList,
  denmarkTaxonomyListLoading = false,
  getDenmarkTaxonomyList,
  selectedHost,
  selectedChip,
  onUpdateTaxonomy,
  onBranchConflictChange,
}) => {
  const [taxonomyDisplayPathById, setTaxonomyDisplayPathById] = useState<
    Record<string, string>
  >({});
  const [taxonomyPathLoadingIds, setTaxonomyPathLoadingIds] = useState<
    Record<string, boolean>
  >({});
  const [taxonomyAncestorCodesById, setTaxonomyAncestorCodesById] = useState<
    Record<string, string[]>
  >({});

  useEffect(() => {
    setTaxonomyDisplayPathById({});
    setTaxonomyPathLoadingIds({});
    setTaxonomyAncestorCodesById({});
  }, [selectedChip]);

  const fetchTaxonomyViewPath = useCallback(
    async (item: { id: string | number; code: string }) => {
      const idKey = String(item.id);
      const code = String(item.code ?? "").trim();
      if (!code) return;

      setTaxonomyPathLoadingIds((prev) => ({ ...prev, [idKey]: true }));
      try {
        const res = await fetchDenmarkTaxonomyByCodeApi({
          codeList: [code],
          detail: true,
          ...(selectedHost ? { hostid: String(selectedHost) } : {}),
        });
        if (res.status !== 200) return;

        const rows = res.data ?? [];
        const row =
          rows.find(
            (r) =>
              String(r.code) === code ||
              String(r.numericalCode) === code ||
              String(r.id) === idKey
          ) ?? rows[0];

        if (row) {
          setTaxonomyDisplayPathById((prev) => ({
            ...prev,
            [idKey]: taxonomyFetchRowToPath(row),
          }));
          setTaxonomyAncestorCodesById((prev) => ({
            ...prev,
            [idKey]: taxonomyAncestorCodesFromRow(row),
          }));
        }
      } finally {
        setTaxonomyPathLoadingIds((prev) => {
          const next = { ...prev };
          delete next[idKey];
          return next;
        });
      }
    },
    []
  );

  const taxonomyHierarchyConflictIds = useMemo(
    () =>
      computeTaxonomyHierarchyConflictIds(
        selectedTaxonomy as { id: string | number; code?: string }[],
        taxonomyAncestorCodesById
      ),
    [selectedTaxonomy, taxonomyAncestorCodesById]
  );

  const hasBranchConflict = taxonomyHierarchyConflictIds.size > 0;

  useEffect(() => {
    onBranchConflictChange?.(hasBranchConflict);
  }, [hasBranchConflict, onBranchConflictChange]);

  const handleAdd = (item: DenmarkTaxonomyChip) => {
    if (selectedTaxonomy.some((t) => String(t.id) === String(item.id))) {
      void fetchTaxonomyViewPath(item);
      return;
    }
    onUpdateTaxonomy([...selectedTaxonomy, item]);
    void fetchTaxonomyViewPath(item);
  };

  const handleRemove = (idKey: string, taxonomyId: string | number) => {
    setTaxonomyDisplayPathById((prev) => {
      if (!(idKey in prev)) return prev;
      const next = { ...prev };
      delete next[idKey];
      return next;
    });
    setTaxonomyAncestorCodesById((prev) => {
      if (!(idKey in prev)) return prev;
      const next = { ...prev };
      delete next[idKey];
      return next;
    });
    onUpdateTaxonomy(selectedTaxonomy.filter((item) => item.id !== taxonomyId));
  };

  return (
    <div className="mb-4">
      <div className="d-flex align-items-center gap-2 mb-3">
        <i className="bi bi-folder fs-5 text-secondary"></i>
        <h6 className="mb-0 text-uppercase fw-semibold">Taxonomy</h6>
      </div>
      <TaxonomySearch
        taxonomyList={denmarkTaxonomyList || []}
        selectedTaxonomy={selectedTaxonomy}
        isSearchLoading={denmarkTaxonomyListLoading}
        onSearch={(keyword) =>
          getDenmarkTaxonomyList({
            search: keyword,
            hostid: String(selectedHost),
            detail: true,
          })
        }
        onAdd={handleAdd}
      />
      {selectedTaxonomy.length > 0 && (
        <div className="mt-2 " style={{ overflow: "hidden" }}>
          {selectedTaxonomy.map((t, idx) => {
            const idKey = String(t.id);
            const isHierarchyConflict = taxonomyHierarchyConflictIds.has(idKey);
            const conflictStyle = isHierarchyConflict
              ? ({ color: "#b02a37" } as const)
              : undefined;
            return (
              <div
                key={t.id}
                className="d-flex align-items-start gap-3 px-3 py-2 w-100"
                style={{
                  borderTop: idx > 0 ? "1px solid #f0f0f0" : "none",
                  background: "#fff",
                }}
              >
                <i
                  className={`bi bi-dash-circle flex-shrink-0 ${
                    isHierarchyConflict ? "text-danger" : "text-secondary"
                  }`}
                  style={{
                    cursor: "pointer",
                    fontSize: 15,
                    marginTop: 3,
                  }}
                  onClick={() => handleRemove(idKey, t.id)}
                />
                <div
                  className="d-flex flex-column"
                  style={{
                    flex: "0 1 42%",
                    minWidth: 120,
                    maxWidth: "48%",
                  }}
                >
                  <div
                    className="fw-semibold"
                    style={{
                      fontSize: 13,
                      wordBreak: "break-word",
                      ...conflictStyle,
                    }}
                  >
                    {t.name}
                  </div>
                  <div
                    className={isHierarchyConflict ? "" : "text-muted"}
                    style={{
                      fontSize: 11,
                      marginTop: 2,
                      wordBreak: "break-word",
                      ...(isHierarchyConflict ? conflictStyle : {}),
                    }}
                  >
                    {t.code}
                  </div>
                </div>
                <div
                  className={isHierarchyConflict ? "" : "text-muted"}
                  style={{
                    flex: "1 1 0",
                    minWidth: 0,
                    fontSize: 11,
                    lineHeight: 1.45,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    ...(isHierarchyConflict ? conflictStyle : {}),
                  }}
                >
                  {taxonomyPathLoadingIds[String(t.id)] ? (
                    <span className="fst-italic">Loading path…</span>
                  ) : taxonomyDisplayPathById[String(t.id)] ? (
                    taxonomyDisplayPathById[String(t.id)]
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DenmarkTaxonomySection;
